import type { Identifier, RaRecord } from 'react-admin';

import { readProjectSettings } from './adminSettings';
import { readAdminSession } from './authProvider';

export const ADMIN_FEEDBACK_RESOURCE = 'feedback' as const;

type AdminApiEnvelope<T> = {
    success?: boolean;
    data?: T;
    statusCode?: number;
    errorMessage?: string | null;
    message?: string;
    error?: string;
    title?: string;
    detail?: string;
};

export type AdminFeedbackRecord = RaRecord & {
    id: string;
    feedbackId: string;
    [key: string]: unknown;
};

function trimTrailingSlash(value: string): string {
    return value.replace(/\/+$/, '');
}

function trimSlashes(value: string): string {
    return value.replace(/^\/+|\/+$/g, '');
}

function hasVersionedApiPath(value: string): boolean {
    return /\/api\/v[^/]+$/i.test(trimTrailingSlash(value));
}

function getAdminApiConfig() {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const baseUrl = trimTrailingSlash(
        String(
            import.meta.env.VITE_API_BASE_URL ??
                import.meta.env.VITE_API_URL ??
                settings.adminApiBaseUrl ??
                'https://test-api.pteultra.com/api/v1',
        ),
    );
    const apiVersion = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const adminToken = String(
        session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '',
    );

    return { baseUrl, apiVersion, adminToken };
}

function getErrorMessage(payload: unknown, fallback: string): string {
    if (typeof payload === 'string' && payload.trim()) return payload;
    if (payload && typeof payload === 'object') {
        const candidate = payload as Record<string, unknown>;
        const direct =
            candidate.errorMessage ?? candidate.message ?? candidate.error ?? candidate.title ?? candidate.detail;
        if (typeof direct === 'string' && direct.trim()) return direct;
    }
    return fallback;
}

function unwrapAdminApiPayload<T>(payload: T | AdminApiEnvelope<T>): T {
    if (!payload || typeof payload !== 'object' || !('data' in payload)) {
        return payload as T;
    }

    const envelope = payload as AdminApiEnvelope<T>;

    if (envelope.success === false) {
        throw new Error(getErrorMessage(envelope, 'Admin Feedback request failed'));
    }

    if (envelope.data !== undefined) {
        return envelope.data;
    }

    return payload as T;
}

function normalizeFeedbackRecord(item: unknown, fallbackId?: Identifier): AdminFeedbackRecord {
    const record = (item ?? {}) as Record<string, unknown>;
    const rawId = record.id ?? record.feedbackId ?? fallbackId;

    if (rawId === undefined || rawId === null || rawId === '') {
        throw new Error('Feedback item is missing feedbackId');
    }

    return {
        ...record,
        id: String(rawId),
        feedbackId: String(record.feedbackId ?? rawId),
    };
}

function asObject(value: unknown): Record<string, unknown> {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        return value as Record<string, unknown>;
    }

    return {};
}

function getListPayloadObject(payload: unknown): Record<string, unknown> | null {
    const unwrapped = unwrapAdminApiPayload(
        payload as Record<string, unknown> | AdminApiEnvelope<Record<string, unknown>>,
    );

    if (unwrapped && typeof unwrapped === 'object' && !Array.isArray(unwrapped)) {
        return unwrapped as Record<string, unknown>;
    }

    return null;
}

function getListRecords<T>(payload: unknown): T[] {
    const unwrapped = unwrapAdminApiPayload(payload as T[] | AdminApiEnvelope<T[]>);

    if (Array.isArray(unwrapped)) {
        return unwrapped;
    }

    if (unwrapped && typeof unwrapped === 'object') {
        const candidate = unwrapped as Record<string, unknown>;
        if (Array.isArray(candidate.items)) {
            return candidate.items as T[];
        }
        if (Array.isArray(candidate.data)) {
            return candidate.data as T[];
        }
    }

    return [];
}

function getListTotal(payload: unknown, fallback: number): number {
    const unwrapped = getListPayloadObject(payload);

    if (unwrapped) {
        if (typeof unwrapped.total === 'number') return unwrapped.total;
        if (typeof unwrapped.totalCount === 'number') return unwrapped.totalCount;
        if (typeof unwrapped.count === 'number') return unwrapped.count;
        if (typeof unwrapped.pageSize === 'number' && typeof unwrapped.totalPages === 'number') {
            return unwrapped.pageSize * unwrapped.totalPages;
        }
    }

    return fallback;
}

function toQueryValue(value: unknown): string {
    if (value === undefined || value === null) return '';
    return String(value).trim();
}

async function requestAdminFeedback<T>(
    path = '',
    init: RequestInit = {},
    query?: Record<string, string>,
): Promise<T> {
    const { baseUrl, apiVersion, adminToken } = getAdminApiConfig();
    const apiRoot = hasVersionedApiPath(baseUrl) ? baseUrl : `${baseUrl}/api/v${apiVersion}`;
    const requestPath = trimSlashes(path);
    const url = new URL(requestPath ? `${apiRoot}/AdminFeedback/${requestPath}` : `${apiRoot}/AdminFeedback`);

    for (const [key, value] of Object.entries(query ?? {})) {
        if (value !== '') {
            url.searchParams.set(key, value);
        }
    }

    const headers = new Headers(init.headers);
    if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json');
    }
    if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }
    if (adminToken && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${adminToken}`);
    }

    const response = await fetch(url.toString(), {
        ...init,
        headers,
    });

    const contentType = response.headers.get('content-type') ?? '';
    const payload = contentType.includes('application/json') ? await response.json() : await response.text();

    if (!response.ok) {
        throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    }

    if (payload && typeof payload === 'object' && 'success' in (payload as Record<string, unknown>)) {
        const envelope = payload as AdminApiEnvelope<unknown>;
        if (envelope.success === false) {
            throw new Error(getErrorMessage(envelope, 'Admin Feedback request failed'));
        }
    }

    return payload as T;
}

export function isAdminFeedbackResource(resource: string): resource is typeof ADMIN_FEEDBACK_RESOURCE {
    return resource === ADMIN_FEEDBACK_RESOURCE;
}

export async function getAdminFeedbackList(params: any) {
    const filter = (params.filter ?? {}) as Record<string, unknown>;
    const { page = 1, perPage = 10 } = params.pagination ?? {};
    const query = {
        page: String(page),
        pageSize: String(perPage),
        search: toQueryValue(filter.search ?? filter.q ?? ''),
        status: toQueryValue(filter.status ?? ''),
    };

    const payload = await requestAdminFeedback<unknown>('', { method: 'GET' }, query);
    const records = getListRecords<unknown>(payload).map((item) => normalizeFeedbackRecord(item));

    return {
        data: records,
        total: getListTotal(payload, records.length),
    };
}

export async function getAdminFeedbackOne(params: any) {
    const payload = unwrapAdminApiPayload(
        await requestAdminFeedback<unknown | AdminApiEnvelope<unknown>>(String(params.id), {
            method: 'GET',
        }),
    );

    return { data: normalizeFeedbackRecord(payload, params.id) };
}

export async function getManyAdminFeedback(params: any) {
    const data = await Promise.all(
        params.ids.map(async (id: Identifier) => {
            const payload = unwrapAdminApiPayload(
                await requestAdminFeedback<unknown | AdminApiEnvelope<unknown>>(String(id), {
                    method: 'GET',
                }),
            );

            return normalizeFeedbackRecord(payload, id);
        }),
    );

    return { data };
}

export async function createAdminFeedback() {
    throw new Error('Creating feedback is not supported.');
}

export async function updateAdminFeedback(params: any) {
    const payload = unwrapAdminApiPayload(
        await requestAdminFeedback<unknown | AdminApiEnvelope<unknown>>('', {
            method: 'POST',
            body: JSON.stringify({
                feedbackId: params.data?.feedbackId ?? params.id,
                status: params.data?.status ?? params.previousData?.status ?? '',
            }),
        }),
    );

    const nextRecord = {
        ...asObject(params.previousData),
        ...asObject(payload),
        feedbackId: params.data?.feedbackId ?? params.id,
        status: params.data?.status ?? params.previousData?.status ?? '',
    };

    return { data: normalizeFeedbackRecord(nextRecord, params.id) };
}

export async function deleteAdminFeedback(params: any) {
    const existing = params.previousData
        ? normalizeFeedbackRecord(params.previousData, params.id)
        : (await getAdminFeedbackOne(params)).data;

    await requestAdminFeedback(String(params.id), {
        method: 'DELETE',
    });

    return { data: existing };
}